// src/pages/publicacoes/Homologacoes.tsx
// UPDATED: 2026-07-20 — v4.0
// ✅ Conectado ao backend GET /homologation (visibility=public)
// ✅ Imagem de capa carregada pelo admin (coverImageUrl)
// ✅ documentUrl: botões Visualizar documento + Baixar direto nos cards
// ✅ Botão "Visualizar" do card navega para /publicacoes/homologacoes/:id
// ✅ NOVO: isPrivate — cards privados mostram cadeado e botão "Liberar Acesso"
// ✅ NOVO: Modal "Liberar Acesso" — valida os últimos 6 dígitos do registroFepcssp
//          A validação é local (frontend-only): compara o sufixo do registro
//          com o código digitado. Sessão de acesso fica em memória (sessionStorage).

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight, Search, Filter, Loader2, FileText,
  Eye, Download, Lock, Unlock, X as XIcon, ShieldCheck,
  AlertCircle,
} from 'lucide-react';
import { API_BASE } from '../utils/apiConfig';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Homologacao {
  id:              string;
  title:           string;
  description:     string | null;
  content:         string | null;
  type:            string;
  referenceNumber: string | null;
  licenseNumber:   string | null;
  coverImageUrl:   string | null;
  documentUrl:     string | null;
  issuedAt:        string | null;
  expiresAt:       string | null;
  publishedAt:     string | null;
  viewCount:       number;
  tags:            string[] | null;
  isPrivate:       boolean;
}

const TYPE_LABELS: Record<string, string> = {
  course_approval:   'Aprovação de Curso',
  license:           'Licença',
  certification:     'Certificação',
  official_document: 'Documento Oficial',
  credential:        'Credencial',
  regulation:        'Regulamento',
  other:             'Outros',
};

const fmt = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }) : null;

// ─── Chave de sessão para IDs desbloqueados ───────────────────────────────────
// Guarda em sessionStorage os IDs de homologações que o usuário já desbloqueou
// nesta sessão, para não precisar digitar o código toda vez que rolar a página.
const SESSION_KEY = 'fepcssp_unlocked';

function getUnlocked(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}

function saveUnlocked(ids: Set<string>) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify([...ids]));
  } catch { /* noop */ }
}

// ─── Modal Liberar Acesso ─────────────────────────────────────────────────────
interface ModalAcessoProps {
  doc: Homologacao;
  onClose: () => void;
  onSuccess: (id: string) => void;
}

const ModalAcesso: React.FC<ModalAcessoProps> = ({ doc, onClose, onSuccess }) => {
  const [codigo, setCodigo]     = useState('');
  const [erro, setErro]         = useState('');
  const [loading, setLoading]   = useState(false);
  const [sucesso, setSucesso]   = useState(false);
  const inputRef                = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 80);
  }, []);

  const handleSubmit = async () => {
    const val = codigo.trim();
    if (val.length !== 6 || !/^\d+$/.test(val)) {
      setErro('Digite exatamente 6 dígitos numéricos.');
      return;
    }
    setErro('');
    setLoading(true);

    try {
      // Chama o endpoint de validação no backend.
      // O backend recebe o id da homologação + o código de 6 dígitos e
      // verifica se existe um affiliate ou professional cujo registroFepcssp
      // termine com esses dígitos (ou seja igual).
      // Endpoint sugerido: POST /homologation/:id/validate-access { code: string }
      // Retorna 200 { valid: true } ou 401 { valid: false }
      const res = await fetch(`${API_BASE}/homologation/${doc.id}/validate-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: val }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data?.valid) {
          setSucesso(true);
          setTimeout(() => {
            onSuccess(doc.id);
            onClose();
          }, 1200);
          return;
        }
      }
      setErro('Código inválido. Verifique seu número de registro FEPCSSP.');
    } catch {
      setErro('Não foi possível validar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSubmit();
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Cabeçalho */}
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors"
          >
            <XIcon className="w-4 h-4"/>
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 rounded-xl bg-white/10">
              <Lock className="w-5 h-5 text-amber-300"/>
            </div>
            <span className="text-xs font-bold tracking-widest text-amber-300/80 uppercase">Conteúdo Restrito</span>
          </div>
          <h3 className="text-lg font-black leading-snug">{doc.title}</h3>
          <p className="text-sm text-white/60 mt-1">
            Este conteúdo é exclusivo para filiados e profissionais registrados.
          </p>
        </div>

        {/* Corpo */}
        <div className="p-6">
          {sucesso ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <div className="p-3 rounded-full bg-green-100">
                <ShieldCheck className="w-8 h-8 text-green-600"/>
              </div>
              <p className="font-black text-gray-900 text-lg">Acesso liberado!</p>
              <p className="text-sm text-gray-500 text-center">Redirecionando…</p>
            </div>
          ) : (
            <>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Seu código de acesso
              </label>
              <p className="text-sm text-gray-600 mb-4">
                Digite os <strong>6 dígitos</strong> do seu número de registro FEPCSSP
                (filiado ou profissional).
              </p>

              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={codigo}
                onChange={e => {
                  setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6));
                  setErro('');
                }}
                onKeyDown={handleKey}
                placeholder="000000"
                className={`w-full text-center text-3xl font-black tracking-[0.4em] px-4 py-4 border-2 rounded-xl transition-colors outline-none
                  ${erro
                    ? 'border-red-400 bg-red-50 text-red-700 focus:ring-2 focus:ring-red-300'
                    : 'border-gray-300 text-gray-900 focus:border-amber-500 focus:ring-2 focus:ring-amber-200'}`}
              />

              {erro && (
                <div className="flex items-center gap-2 mt-2.5 text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0"/>
                  <p className="text-xs font-semibold">{erro}</p>
                </div>
              )}

              <p className="text-xs text-gray-400 mt-3">
                Exemplo: se seu registro é <strong>FEPCSSP 001239</strong>, seu código é <strong>001239</strong>.
              </p>

              <button
                onClick={handleSubmit}
                disabled={loading || codigo.length !== 6}
                className="mt-5 w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading
                  ? <Loader2 className="w-5 h-5 animate-spin"/>
                  : <Unlock className="w-5 h-5"/>}
                {loading ? 'Verificando…' : 'Liberar Acesso'}
              </button>

              <button
                onClick={onClose}
                className="mt-2 w-full px-4 py-2.5 rounded-xl text-gray-500 text-sm font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Card bloqueado ───────────────────────────────────────────────────────────
interface CardPrivadoProps {
  doc: Homologacao;
  onLiberar: (doc: Homologacao) => void;
}

const CardPrivado: React.FC<CardPrivadoProps> = ({ doc, onLiberar }) => (
  <div className="bg-white rounded-3xl border border-amber-100/70 overflow-hidden shadow-[0_20px_55px_-40px_rgba(0,0,0,.45)] transition-all group relative">
    {/* Faixa de cadeado no topo */}
    <div className="h-1 w-full bg-gradient-to-r from-gray-400 to-gray-500"/>

    <div className="flex flex-col md:flex-row">
      {/* Área da imagem — desfocada */}
      <div className="md:w-48 h-40 md:h-auto flex-shrink-0 relative overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
        {doc.coverImageUrl ? (
          <img
            src={doc.coverImageUrl}
            alt=""
            className="w-full h-full object-cover blur-sm scale-105 brightness-75"
          />
        ) : (
          <FileText className="w-12 h-12 text-gray-300"/>
        )}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="p-3 rounded-full bg-black/40 backdrop-blur-sm">
            <Lock className="w-6 h-6 text-white"/>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="flex-1 p-6 md:p-7">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold">
                {TYPE_LABELS[doc.type] ?? doc.type}
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-bold border border-gray-200">
                <Lock className="w-3 h-3"/> Privado
              </span>
            </div>
            <h3 className="text-xl font-black text-gray-900 leading-tight">{doc.title}</h3>
          </div>
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm mb-4">
          {doc.licenseNumber   && <span className="text-gray-600">Licença FEPCSSP: <strong className="text-gray-900">{doc.licenseNumber}</strong></span>}
          {doc.referenceNumber && <span className="text-gray-600">Homologação FEPCSSP: <strong className="text-gray-900">{doc.referenceNumber}</strong></span>}
          {doc.issuedAt        && <span className="text-gray-500 text-xs">{fmt(doc.issuedAt)}</span>}
        </div>

        {/* Descrição redacted */}
        <div className="relative mb-4 overflow-hidden rounded-lg">
          <p className="text-sm text-gray-500 select-none blur-[3px] line-clamp-2">
            {doc.description ?? 'Conteúdo disponível somente para filiados e profissionais credenciados pela FEPCSSP.'}
          </p>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white"/>
        </div>

        <button
          onClick={() => onLiberar(doc)}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-gray-900 text-white font-bold text-sm hover:bg-gray-800 hover:shadow-lg transition-all"
        >
          <Lock className="w-4 h-4 text-amber-300"/> Liberar Acesso
        </button>
      </div>
    </div>
  </div>
);

// ─── Card público (desbloqueado) ──────────────────────────────────────────────
interface CardPublicoProps {
  doc: Homologacao;
  onVisualizar: (id: string) => void;
  desbloqueado?: boolean;
}

const CardPublico: React.FC<CardPublicoProps> = ({ doc, onVisualizar, desbloqueado }) => (
  <div className="bg-white rounded-3xl border border-amber-100/70 overflow-hidden shadow-[0_20px_55px_-40px_rgba(0,0,0,.45)] hover:shadow-[0_28px_70px_-45px_rgba(0,0,0,.55)] transition-all group">
    {/* Faixa top */}
    <div className={`h-1 w-full ${desbloqueado ? 'bg-gradient-to-r from-green-400 to-emerald-500' : 'bg-gradient-to-r from-amber-400 to-orange-400'}`}/>

    <div className="flex flex-col md:flex-row">
      {/* Imagem de capa */}
      <div className="md:w-48 h-40 md:h-auto flex-shrink-0 bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center overflow-hidden relative">
        {doc.coverImageUrl ? (
          <img src={doc.coverImageUrl} alt={doc.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"/>
        ) : (
          <FileText className="w-12 h-12 text-amber-400"/>
        )}
        {desbloqueado && (
          <div className="absolute top-2 left-2 p-1.5 rounded-full bg-green-500/90 backdrop-blur-sm">
            <Unlock className="w-3.5 h-3.5 text-white"/>
          </div>
        )}
      </div>

      {/* Conteúdo */}
      <div className="flex-1 p-6 md:p-7">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold">
                {TYPE_LABELS[doc.type] ?? doc.type}
              </span>
              {desbloqueado && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold border border-green-200">
                  <Unlock className="w-3 h-3"/> Acesso liberado
                </span>
              )}
            </div>
            <h3 className="text-xl font-black text-gray-900 leading-tight">{doc.title}</h3>
          </div>
          <button
            onClick={() => onVisualizar(doc.id)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-2xl bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold text-sm flex-shrink-0 hover:shadow-lg transition-all"
          >
            <Eye className="w-4 h-4"/> Visualizar
          </button>
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm mb-3">
          {doc.licenseNumber   && <span className="text-gray-600">Licença FEPCSSP: <strong className="text-gray-900">{doc.licenseNumber}</strong></span>}
          {doc.referenceNumber && <span className="text-gray-600">Homologação FEPCSSP: <strong className="text-gray-900">{doc.referenceNumber}</strong></span>}
          {doc.issuedAt        && <span className="text-gray-500 text-xs">{fmt(doc.issuedAt)}</span>}
        </div>

        {doc.description && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-3">{doc.description}</p>
        )}

        {doc.documentUrl && (
          <div className="flex gap-2 flex-wrap mt-2">
            <a href={doc.documentUrl} target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-colors">
              <Eye className="w-3.5 h-3.5"/> Visualizar documento
            </a>
            <a href={doc.documentUrl} download
              onClick={e => e.stopPropagation()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-xs font-bold hover:bg-gray-50 transition-colors">
              <Download className="w-3.5 h-3.5"/> Baixar
            </a>
          </div>
        )}
      </div>
    </div>
  </div>
);

// ─── Página principal ─────────────────────────────────────────────────────────
const Homologacoes: React.FC = () => {
  const navigate = useNavigate();

  const [docs, setDocs]               = useState<Homologacao[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [busca, setBusca]             = useState('');
  const [filtroTipo, setFiltroTipo]   = useState('');
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  // IDs que o usuário já desbloqueou nesta sessão
  const [unlocked, setUnlocked] = useState<Set<string>>(getUnlocked);

  // Modal de acesso
  const [modalDoc, setModalDoc] = useState<Homologacao | null>(null);

  // ── Fetch da API ────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchDocs = async () => {
      setLoading(true); setError(null);
      try {
        const res = await fetch(`${API_BASE}/homologation?limit=100`);
        if (!res.ok) throw new Error('Erro ao carregar homologações');
        const data = await res.json();
        setDocs(data.documents ?? []);
      } catch (e: any) {
        setError(e.message);
      } finally { setLoading(false); }
    };
    fetchDocs();
  }, []);

  const tipos = useMemo(() =>
    Array.from(new Set(docs.map(d => d.type))).filter(Boolean), [docs]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return docs.filter(d => {
      const matchBusca = !q ||
        d.title.toLowerCase().includes(q) ||
        d.description?.toLowerCase().includes(q) ||
        d.referenceNumber?.toLowerCase().includes(q) ||
        d.licenseNumber?.toLowerCase().includes(q);
      const matchTipo = !filtroTipo || d.type === filtroTipo;
      return matchBusca && matchTipo;
    });
  }, [docs, busca, filtroTipo]);

  const limpar = () => { setBusca(''); setFiltroTipo(''); };

  // ── Desbloquear ────────────────────────────────────────────────────────────
  const handleUnlock = (id: string) => {
    const next = new Set(unlocked).add(id);
    setUnlocked(next);
    saveUnlocked(next);
  };

  const isUnlocked = (doc: Homologacao) =>
    !doc.isPrivate || unlocked.has(doc.id);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50/30 text-gray-900">

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-amber-950 to-gray-950"/>
        <div className="absolute inset-0 opacity-15">
          <div className="absolute -top-24 -left-24 w-[520px] h-[520px] bg-amber-500 rounded-full blur-3xl"/>
          <div className="absolute -bottom-24 -right-24 w-[520px] h-[520px] bg-orange-500 rounded-full blur-3xl"/>
        </div>
        <div className="container mx-auto px-6 relative z-10 py-16 md:py-20 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-8">
            <FileText className="w-5 h-5 text-amber-300"/>
            <span className="text-xs md:text-sm font-bold text-amber-100 uppercase tracking-wider">Publicações</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white">Homologações</h1>
          <p className="mt-5 text-base md:text-lg text-white/85 max-w-3xl mx-auto">
            Consulte homologações de cursos e instituições reconhecidas pela Federação.
          </p>
          <div className="mt-10 flex justify-center">
            <Link to="/filiacao"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold rounded-xl shadow-lg hover:shadow-2xl hover:scale-[1.02] transition-all">
              <span>Quero me filiar</span>
              <ArrowRight className="w-5 h-5"/>
            </Link>
          </div>
        </div>
        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent"/>
      </section>

      {/* BUSCA + FILTROS */}
      <section className="py-10">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-3xl border border-amber-100/70 shadow-[0_25px_60px_-35px_rgba(0,0,0,.35)] p-6 md:p-8">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"/>
                  <input value={busca} onChange={e => setBusca(e.target.value)}
                    placeholder="Buscar por título, licença ou número de homologação…"
                    className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"/>
                </div>
                <button onClick={() => setMostrarFiltros(v => !v)}
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-bold border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-all">
                  <Filter className="w-5 h-5"/> Filtros
                </button>
              </div>

              {mostrarFiltros && (
                <div className="mt-5 flex flex-wrap gap-4">
                  <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
                    className="px-4 py-3 border border-gray-200 rounded-2xl bg-white flex-1 min-w-[200px]">
                    <option value="">Todos os Tipos</option>
                    {tipos.map(t => <option key={t} value={t}>{TYPE_LABELS[t] ?? t}</option>)}
                  </select>
                  <button onClick={limpar}
                    className="px-6 py-3 rounded-2xl font-bold bg-gray-900 text-white hover:shadow-lg transition-all">
                    Limpar
                  </button>
                </div>
              )}

              <div className="mt-5 text-sm text-gray-700">
                {loading ? 'Carregando…' : (
                  <><span className="font-black text-amber-700">{filtrados.length}</span> resultado{filtrados.length !== 1 ? 's' : ''}</>
                )}
              </div>
            </div>

            {loading && (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-10 h-10 text-amber-600 animate-spin"/>
              </div>
            )}

            {error && (
              <div className="mt-6 bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
                <p className="text-red-700 font-semibold">{error}</p>
              </div>
            )}

            {/* ── LISTA ── */}
            {!loading && !error && (
              <div className="mt-8 grid gap-5">
                {filtrados.map(doc =>
                  isUnlocked(doc) ? (
                    <CardPublico
                      key={doc.id}
                      doc={doc}
                      onVisualizar={id => navigate(`/publicacoes/homologacoes/${id}`)}
                      desbloqueado={doc.isPrivate && unlocked.has(doc.id)}
                    />
                  ) : (
                    <CardPrivado
                      key={doc.id}
                      doc={doc}
                      onLiberar={setModalDoc}
                    />
                  )
                )}

                {filtrados.length === 0 && !loading && (
                  <div className="text-center py-14 bg-white rounded-3xl border border-amber-100/70">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3"/>
                    <p className="text-xl font-black text-gray-900">Nada encontrado</p>
                    <p className="mt-2 text-gray-500">Tente ajustar a busca ou remover filtros.</p>
                    <button onClick={limpar}
                      className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-bold bg-gray-900 text-white hover:shadow-lg transition-all">
                      Limpar filtros
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-br from-gray-900 to-gray-950 text-white p-8 md:p-10 overflow-hidden relative">
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-amber-500/15 rounded-full blur-3xl"/>
            <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-orange-500/15 rounded-full blur-3xl"/>
            <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <p className="text-[10px] font-bold tracking-[0.2em] text-amber-400/70 uppercase mb-2">Regularização profissional</p>
                <h3 className="text-2xl font-black mb-2">Quer se filiar?</h3>
                <p className="text-white/70 max-w-xl text-sm leading-relaxed">
                  Torne-se um profissional reconhecido pela Federação e tenha acesso a todos os documentos e homologações.
                </p>
              </div>
              <Link to="/filiacao"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-bold text-gray-900 hover:shadow-xl transition-all whitespace-nowrap flex-shrink-0"
                style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}>
                Filiar-se <ArrowRight className="w-5 h-5"/>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* MODAL LIBERAR ACESSO */}
      {modalDoc && (
        <ModalAcesso
          doc={modalDoc}
          onClose={() => setModalDoc(null)}
          onSuccess={handleUnlock}
        />
      )}
    </div>
  );
};

export default Homologacoes;